import { Link } from 'react-router-dom';
import { AlertCircle, Clock, ExternalLink } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { STAGE_LABELS, PipelineStage } from '@/types';

export interface AttentionItem {
  id: string;
  candidateId: string;
  candidateName: string;
  jobId: string;
  jobTitle: string;
  currentStage: PipelineStage;
  daysStuck: number;
}

interface AttentionListProps {
  items: AttentionItem[] | null;
  isLoading: boolean;
}

function getDaysStuckBadgeVariant(days: number): 'warning' | 'destructive' {
  return days >= 14 ? 'destructive' : 'warning';
}

export function AttentionList({ items, isLoading }: AttentionListProps) {
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-warning-500" />
            Needs Attention
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="p-3 rounded-lg border">
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-5 w-16 rounded-full" />
                  </div>
                  <Skeleton className="h-3 w-24" />
                  <Skeleton className="h-3 w-20" />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <AlertCircle className="h-5 w-5 text-warning-500" />
          Needs Attention
          {items && items.length > 0 && (
            <Badge variant="warning" className="ml-auto">
              {items.length}
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {!items || items.length === 0 ? (
          <div className="h-48 flex flex-col items-center justify-center text-text-tertiary text-sm gap-2">
            <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
              <AlertCircle className="h-5 w-5 text-green-500" />
            </div>
            <span>All applications are on track!</span>
          </div>
        ) : (
          <div className="space-y-3 max-h-80 overflow-y-auto">
            {items.map((item) => (
              <div
                key={item.id}
                className="p-3 rounded-lg border border-white/10 hover:border-white/20 transition-colors"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <Link
                      to={`/candidates/${item.candidateId}`}
                      state={{
                        attentionContext: {
                          applicationId: item.id,
                          jobId: item.jobId,
                          jobTitle: item.jobTitle,
                          currentStage: item.currentStage,
                          daysStuck: item.daysStuck,
                        },
                      }}
                      className="font-medium text-text-primary hover:text-emerald-500 block truncate"
                    >
                      {item.candidateName}
                    </Link>
                    <Link
                      to={`/jobs/${item.jobId}/pipeline`}
                      className="text-sm text-text-tertiary hover:text-text-secondary flex items-center gap-1 mt-0.5"
                    >
                      {item.jobTitle}
                      <ExternalLink className="h-3 w-3" />
                    </Link>
                  </div>
                  <Badge variant={getDaysStuckBadgeVariant(item.daysStuck)}>
                    <Clock className="h-3 w-3 mr-1" />
                    {item.daysStuck}d
                  </Badge>
                </div>
                <div className="mt-2 text-xs text-text-tertiary">
                  Stuck at: {STAGE_LABELS[item.currentStage]}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
